'use client';

import { useCallback, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Message } from '../ChatWidget.interfaces';
import { setChatListenerReady } from '../lib/globalChatService';
import { streamChatResponse, toChatbaseMessages } from '../lib/chatAPI';

/**
 * Message state helpers
 */
const addUserMessage = (messages: Message[], text: string): Message[] => {
  return [...messages, { text, role: 'user', id: uuidv4() }];
};

const addBotMessage = (messages: Message[], text: string, id: string = uuidv4()): Message[] => {
  return [...messages, { text, role: 'bot', id }];
};

const updateBotMessageById = (messages: Message[], botId: string, text: string): Message[] => {
  return messages.map((msg) => (msg.id === botId && msg.role === 'bot' ? { ...msg, text } : msg));
};

interface UseChatReturn {
  messages: Message[];
  conversationId: string | null;
  isTyping: boolean;
  sendMessage: (message: string) => Promise<void>;
}

export const useChatLogic = (isOpen: boolean): UseChatReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize when chat opens
  useEffect(() => {
    if (isOpen) {
      setChatListenerReady(true);

      if (!initialized) {
        setInitialized(true);
      }
    } else {
      // Cleanup on close
      setChatListenerReady(false);
    }
  }, [isOpen, initialized]);

  /**
   * Send a message to Chatbase API and stream response
   */
  const chatWithChatbase = useCallback(
    async (userMessage: string, botMessageId: string, currentConversationId: string) => {
      try {
        // Build message history including the new user message
        const chatbaseMessages = toChatbaseMessages(messages);
        chatbaseMessages.push({ content: userMessage, role: 'user' });

        let accumulatedText = '';

        // Stream response chunks
        for await (const chunk of streamChatResponse(chatbaseMessages, currentConversationId)) {
          accumulatedText += chunk;
          setMessages((prev) => updateBotMessageById(prev, botMessageId, accumulatedText));
        }
      } catch (error) {
        console.error('Chatbase API error:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Sorry, I encountered an error. Please try again.';
        setMessages((prev) => updateBotMessageById(prev, botMessageId, errorMessage));
      } finally {
        setIsTyping(false);
      }
    },
    [messages],
  );

  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim()) return;

      // Add a user message
      setMessages((prev) => addUserMessage(prev, message));

      // Generate conversation ID if needed
      const currentConversationId = conversationId || uuidv4();
      if (!conversationId) {
        setConversationId(currentConversationId);
      }

      // Add a placeholder bot message and set typing
      const botMessageId = uuidv4();
      setMessages((prev) => addBotMessage(prev, '', botMessageId));
      setIsTyping(true);

      // Call Chatbase API
      await chatWithChatbase(message, botMessageId, currentConversationId);
    },
    [initialized, conversationId, chatWithChatbase],
  );

  return {
    messages,
    conversationId,
    isTyping,
    sendMessage,
  };
};
